import { useState, useCallback } from 'react';
import { TermsDisplayMode } from '../components/Terms';

export interface UseTermsOptions {
  displayMode?: TermsDisplayMode;
  showAcceptDecline?: boolean;
  acceptText?: string;
  declineText?: string;
  onAccept?: () => void;
  onDecline?: () => void;
}

export interface UseTermsReturn {
  isOpen: boolean;
  documentPath: string;
  title: string;
  displayMode: TermsDisplayMode;
  showAcceptDecline: boolean;
  acceptText: string;
  declineText: string;
  onAccept?: () => void;
  onDecline?: () => void;
  openTerms: (documentPath: string, title: string, options?: UseTermsOptions) => void;
  openPrivacyPolicy: (options?: UseTermsOptions) => void;
  openTermsAndConditions: (options?: UseTermsOptions) => void;
  closeTerms: () => void;
}

export const useTerms = (): UseTermsReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [documentPath, setDocumentPath] = useState('');
  const [title, setTitle] = useState('');
  const [displayMode, setDisplayMode] = useState<TermsDisplayMode>('popup');
  const [showAcceptDecline, setShowAcceptDecline] = useState(false);
  const [acceptText, setAcceptText] = useState('Accept');
  const [declineText, setDeclineText] = useState('Decline');
  const [onAccept, setOnAccept] = useState<(() => void) | undefined>();
  const [onDecline, setOnDecline] = useState<(() => void) | undefined>();

  const openTerms = useCallback((
    docPath: string,
    docTitle: string,
    options: UseTermsOptions = {}
  ) => {
    setDocumentPath(docPath);
    setTitle(docTitle);
    setDisplayMode(options.displayMode || 'popup');
    setShowAcceptDecline(options.showAcceptDecline || false);
    setAcceptText(options.acceptText || 'Accept');
    setDeclineText(options.declineText || 'Decline');
    setOnAccept(options.onAccept);
    setOnDecline(options.onDecline);
    setIsOpen(true);
  }, []);

  const openPrivacyPolicy = useCallback((options?: UseTermsOptions) => {
    openTerms('privacy-policy.html', 'Privacy Policy', options);
  }, [openTerms]);

  const openTermsAndConditions = useCallback((options?: UseTermsOptions) => {
    openTerms('terms-and-conditions.html', 'Terms and Conditions', options);
  }, [openTerms]);

  const closeTerms = useCallback(() => {
    setIsOpen(false);
    setDocumentPath('');
    setTitle('');
    setDisplayMode('popup');
    setShowAcceptDecline(false);
    setAcceptText('Accept');
    setDeclineText('Decline');
    setOnAccept(undefined);
    setOnDecline(undefined);
  }, []);

  return {
    isOpen,
    documentPath,
    title,
    displayMode,
    showAcceptDecline,
    acceptText,
    declineText,
    onAccept,
    onDecline,
    openTerms,
    openPrivacyPolicy,
    openTermsAndConditions,
    closeTerms,
  };
}; 